// W27 (2026-10-20): the rep queue behind Finish & Next.
//
//   const queue = useRepQueue(customerId)
//
//   queue.current     the record the api-server says the rep is on
//                     (null until the first Finish & Next seeds it)
//   queue.lookahead   next records from GET /v1/reps/:repId/queue/lookahead?n=3
//   queue.advance(outcome) -> POST /v1/reps/:repId/queue/advance {outcome, workId}
//
// Same-origin relative fetch + buildSessionHeaders, exactly like
// useContactProfile / useContactCollections: the sales-workspace container
// has no VITE_RUNTIME_API_BASE_URL, nginx proxies /v1/ to the api-server,
// and the url-bootstrap verifier needs X-Customer-Id / X-Portal-Id /
// X-User-Id / X-User-Email on every call. repId is the session userId
// (the HubSpot user id the card put on the iframe URL).
//
// First click on an empty queue: the api-server's in-memory store
// promotes prefetched -> current on each advance, so when `current` is
// still null after the first POST but `prefetched` is filled we advance
// once more (seeding). A null/null answer is "queue is clear"; a
// `degraded` envelope is a failed HubSpot lookup and is reported as such,
// never as clear.
//
// Status 'skipped' = preview / dev-mock session; the screen renders no
// queue UI at all in that case.

import { useCallback, useEffect, useRef, useState } from 'react';

import { buildSessionHeaders, useContactReadGate } from './useContactProfile.js';

export interface QueueDisplay {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  initials?: string | null;
  phone?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
  hubspotContactId?: string | null;
}

export interface QueueItem {
  customerId: string;
  workId: string | null;
  actionType: string | null;
  display: QueueDisplay | null;
  why: string[];
  source: string | null;
}

export type QueueOutcome =
  | 'reached'
  | 'no_answer'
  | 'left_voicemail'
  | 'callback_requested'
  | 'do_not_call';

export const QUEUE_OUTCOMES: ReadonlyArray<{ value: QueueOutcome; label: string }> = [
  { value: 'reached', label: 'Reached' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'left_voicemail', label: 'Left voicemail' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'do_not_call', label: 'Do not call' },
];

export type QueueStatus = 'skipped' | 'loading' | 'ready' | 'error';

export interface RepQueueState {
  status: QueueStatus;
  repId: string | null;
  current: QueueItem | null;
  prefetched: QueueItem | null;
  errorCode: string | null;
  lookahead: QueueItem[];
  lookaheadStatus: QueueStatus;
  /** Reason string when the api-server reports the engine is not wired. */
  lookaheadDegraded: string | null;
  advancing: boolean;
}

export type AdvanceResult =
  | { kind: 'advanced'; current: QueueItem }
  | { kind: 'clear' }
  | { kind: 'degraded'; errorCode: string }
  | { kind: 'error'; errorCode: string }
  | { kind: 'skipped' };

export interface RepQueue extends RepQueueState {
  advance(outcome: QueueOutcome): Promise<AdvanceResult>;
  refresh(): void;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function normalizeQueueItem(raw: unknown): QueueItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const customerId = asString(r.customerId);
  if (!customerId) return null;
  const display = r.display && typeof r.display === 'object' ? (r.display as QueueDisplay) : null;
  const why = Array.isArray(r.why) ? r.why.filter((w): w is string => typeof w === 'string' && w.length > 0) : [];
  return {
    customerId,
    workId: asString(r.workId),
    actionType: asString(r.actionType),
    display,
    why,
    source: asString(r.source),
  };
}

interface JsonResult {
  ok: boolean;
  status: number;
  body: Record<string, unknown> | null;
  errorCode: string | null;
}

async function requestJson(url: string, init: RequestInit): Promise<JsonResult> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    return { ok: false, status: 0, body: null, errorCode: 'FETCH_FAILED' };
  }
  let body: Record<string, unknown> | null = null;
  try {
    const text = await response.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    body = null;
  }
  if (!response.ok) {
    const code = body && typeof body.errorCode === 'string' ? body.errorCode : `HTTP_${response.status}`;
    return { ok: false, status: response.status, body, errorCode: code };
  }
  return { ok: true, status: response.status, body, errorCode: null };
}

const INITIAL: RepQueueState = {
  status: 'skipped',
  repId: null,
  current: null,
  prefetched: null,
  errorCode: null,
  lookahead: [],
  lookaheadStatus: 'skipped',
  lookaheadDegraded: null,
  advancing: false,
};

export interface RepQueueOptions {
  /**
   * W40.2 (2026-10-21): read only GET /queue/lookahead. The "Up next" card on
   * the screens that do not own Finish & Next (03, 05-10, 16, 17, companions)
   * needs the lookahead alone; `current` stays null and `status` reports
   * 'ready' once the session gate opens.
   */
  lookaheadOnly?: boolean;
}

export function useRepQueue(customerId: string | null | undefined, lookaheadN = 3, options: RepQueueOptions = {}): RepQueue {
  const lookaheadOnly = options.lookaheadOnly === true;
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const repId = canFetch && gate.session ? gate.session.userId : null;
  const [state, setState] = useState<RepQueueState>(INITIAL);
  const stateRef = useRef(state);
  stateRef.current = state;
  const latest = useRef(0);
  const sessionRef = useRef(gate.session);
  sessionRef.current = gate.session;
  const [tick, setTick] = useState(0);

  const queueBase = repId ? `/v1/reps/${encodeURIComponent(repId)}/queue` : null;

  const loadLookahead = useCallback(
    async (requestId: number): Promise<void> => {
      const session = sessionRef.current;
      if (!queueBase || !session) return;
      const r = await requestJson(`${queueBase}/lookahead?n=${encodeURIComponent(String(lookaheadN))}`, {
        method: 'GET',
        headers: buildSessionHeaders(session),
      });
      if (latest.current !== requestId) return;
      if (!r.ok) {
        setState((s) => ({ ...s, lookahead: [], lookaheadStatus: 'error', lookaheadDegraded: r.errorCode }));
        return;
      }
      const list = Array.isArray(r.body?.lookahead) ? (r.body!.lookahead as unknown[]) : [];
      const degraded = r.body?.degraded && typeof r.body.degraded === 'object'
        ? asString((r.body.degraded as Record<string, unknown>).reason)
        : null;
      setState((s) => ({
        ...s,
        lookahead: list.map(normalizeQueueItem).filter((x): x is QueueItem => x !== null),
        lookaheadStatus: 'ready',
        lookaheadDegraded: degraded,
      }));
    },
    [queueBase, lookaheadN],
  );

  useEffect(() => {
    if (!canFetch || !queueBase || !sessionRef.current) {
      setState(INITIAL);
      return;
    }
    const requestId = latest.current + 1;
    latest.current = requestId;
    setState((s) => ({ ...s, status: 'loading', repId, errorCode: null, lookaheadStatus: 'loading' }));
    const session = sessionRef.current;
    if (lookaheadOnly) {
      setState((s) => ({ ...s, status: 'ready', repId, errorCode: null, current: null, prefetched: null }));
      void loadLookahead(requestId);
      return () => {
        if (latest.current === requestId) latest.current = requestId + 1;
      };
    }
    requestJson(`${queueBase}/current`, { method: 'GET', headers: buildSessionHeaders(session) })
      .then((r) => {
        if (latest.current !== requestId) return;
        if (!r.ok) {
          setState((s) => ({ ...s, status: 'error', repId, errorCode: r.errorCode, current: null, prefetched: null }));
          return;
        }
        setState((s) => ({
          ...s,
          status: 'ready',
          repId,
          errorCode: null,
          current: normalizeQueueItem(r.body?.current),
          prefetched: normalizeQueueItem(r.body?.prefetched),
        }));
      });
    void loadLookahead(requestId);
    return () => {
      if (latest.current === requestId) latest.current = requestId + 1;
    };
    // sessionIdentity + sessionToken stand in for the session object so a
    // token refresh re-runs this without closing over every emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, queueBase, repId, customerId, sessionIdentity, sessionToken, tick, loadLookahead, lookaheadOnly]);

  const advance = useCallback(
    async (outcome: QueueOutcome): Promise<AdvanceResult> => {
      const session = sessionRef.current;
      if (!canFetch || !queueBase || !session) return { kind: 'skipped' };
      setState((s) => ({ ...s, advancing: true }));
      const headers = { ...buildSessionHeaders(session), 'Content-Type': 'application/json' };
      const post = (body: Record<string, unknown>) =>
        requestJson(`${queueBase}/advance`, { method: 'POST', headers, body: JSON.stringify(body) });

      // Optimistic lock: send the workId we believe is current so a
      // double click or a second tab gets 409 instead of skipping a record.
      const currentWorkId: string | null = stateRef.current.current?.workId ?? null;

      const finish = (patch: Partial<RepQueueState>, result: AdvanceResult): AdvanceResult => {
        setState((s) => ({ ...s, ...patch, advancing: false }));
        return result;
      };

      let r = await post({ outcome, ...(currentWorkId ? { workId: currentWorkId } : {}) });
      if (!r.ok) {
        if (r.status === 409) {
          // Someone (or a double click) advanced already: resync.
          setTick((t) => t + 1);
        }
        return finish({}, { kind: 'error', errorCode: r.errorCode ?? 'ADVANCE_FAILED' });
      }
      let current = normalizeQueueItem(r.body?.current);
      let prefetched = normalizeQueueItem(r.body?.prefetched);
      if (!current && prefetched) {
        // Seeding: the first advance only fills the prefetch slot.
        r = await post({ outcome, workId: prefetched.workId ?? undefined });
        if (!r.ok) {
          return finish({}, { kind: 'error', errorCode: r.errorCode ?? 'ADVANCE_FAILED' });
        }
        current = normalizeQueueItem(r.body?.current);
        prefetched = normalizeQueueItem(r.body?.prefetched);
      }
      const degraded = r.body?.degraded && typeof r.body.degraded === 'object'
        ? (r.body.degraded as Record<string, unknown>)
        : null;
      if (!current) {
        if (degraded) {
          return finish(
            { current: null, prefetched: null, status: 'ready' },
            { kind: 'degraded', errorCode: asString(degraded.errorCode) ?? asString(degraded.reason) ?? 'SELECT_NEXT_FAILED' },
          );
        }
        return finish({ current: null, prefetched: null, status: 'ready', lookahead: [] }, { kind: 'clear' });
      }
      const requestId = latest.current + 1;
      latest.current = requestId;
      void loadLookahead(requestId);
      return finish({ current, prefetched, status: 'ready', errorCode: null }, { kind: 'advanced', current });
    },
    [canFetch, queueBase, loadLookahead],
  );

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, advance, refresh };
}
